"use client";

import { useRef, useState } from "react";

export type EnquiryStatus = "idle" | "submitting" | "success" | "error";

interface UseEnquirySubmitOptions {
  path: string;
  mapError: (response: Response) => Promise<string>;
  networkError: string;
}

interface UseEnquirySubmitResult {
  status: EnquiryStatus;
  errorMessage: string;
  submit: (body: unknown) => Promise<void>;
  reset: () => void;
}

export function useEnquirySubmit({
  path,
  mapError,
  networkError,
}: UseEnquirySubmitOptions): UseEnquirySubmitResult {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";
  const [status, setStatus] = useState<EnquiryStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const requestId = useRef(0);
  const submitting = useRef(false);

  /** Drops a response that arrives after the form has been reset. */
  function reset() {
    requestId.current += 1;
    submitting.current = false;
    setStatus("idle");
    setErrorMessage("");
  }

  function isCurrent(id: number): boolean {
    return requestId.current === id;
  }

  async function settle(response: Response, id: number): Promise<boolean> {
    if (!isCurrent(id)) return false;
    if (response.ok) {
      setStatus("success");
      return true;
    }

    const message = await mapError(response);
    if (!isCurrent(id)) return false;
    setStatus("error");
    setErrorMessage(message);
    return false;
  }

  async function submit(body: unknown) {
    if (submitting.current) return;

    const id = requestId.current;
    submitting.current = true;
    setStatus("submitting");
    setErrorMessage("");
    let sent = false;

    try {
      const response = await fetch(`${basePath}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      sent = await settle(response, id);
    } catch {
      if (!isCurrent(id)) return;
      setStatus("error");
      setErrorMessage(networkError);
    } finally {
      if (isCurrent(id) && !sent) submitting.current = false;
    }
  }

  return { status, errorMessage, submit, reset };
}
